import { useMemo, useState, type FormEvent } from "react";
import { CalendarClock, Plus, Trash2, X } from "lucide-react";
import { useAuth } from "../lib/auth.js";
import { ApiClient } from "../lib/api.js";

interface Period { startDate: string; endDate: string }
const MAX_BATCH_DATES = 270;

export function ScheduleDialog({open, onClose}: {open: boolean; onClose(): void}) {
  const {accessToken} = useAuth();
  const [periods, setPeriods] = useState<Period[]>([{startDate: "", endDate: ""}]);
  const [executeAt, setExecuteAt] = useState("");
  const [force, setForce] = useState(false);
  const [confirmedLargeBatch, setConfirmedLargeBatch] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const dates = useMemo(() => expandCalendarDates(periods), [periods]);
  const large = dates.length > 30;
  const tooLarge = dates.length > MAX_BATCH_DATES;
  if (!open) return null;

  async function submit(event: FormEvent) {
    event.preventDefault(); setSaving(true); setError("");
    try {
      await new ApiClient(accessToken).createJob({periods,
        executeAt: executeAt ? new Date(executeAt).toISOString() : undefined,
        forceRefresh: force, windowDays: 90,
        acknowledgeLargeBatch: large && confirmedLargeBatch});
      onClose(); window.location.assign("/jobs");
    } catch (caught) { setError(caught instanceof Error ? caught.message : String(caught)); }
    finally { setSaving(false); }
  }

  function updatePeriod(index: number, field: keyof Period, value: string) {
    setPeriods((current) => current.map((period, position) => position === index
      ? {...period, [field]: value} : period));
    setConfirmedLargeBatch(false);
  }

  return <div className="modal-backdrop" role="presentation"><section className="modal schedule-modal" role="dialog" aria-modal="true" aria-labelledby="schedule-title">
    <div className="modal-head"><div><span className="eyebrow">Analysis control</span><h2 id="schedule-title">Run historical dates</h2></div><button className="icon-button" onClick={onClose}><X size={19}/></button></div>
    <form onSubmit={submit}>
      <div className="period-heading"><div><strong>Analysis periods</strong><small>Every calendar date is tracked. Weekends are certified as no activity. Overlapping dates are counted once.</small></div>
        <button type="button" className="button secondary compact" disabled={periods.length >= 10}
          onClick={() => setPeriods((current) => [...current, {startDate: "", endDate: ""}])}><Plus size={14}/> Add period</button></div>
      <div className="period-list">{periods.map((period, index) => <div className="period-row" key={index}>
        <label>Start date<input type="date" required value={period.startDate} max={yesterday()}
          onChange={(event) => updatePeriod(index, "startDate", event.target.value)}/></label>
        <label>End date<input type="date" required value={period.endDate} min={period.startDate || undefined}
          max={yesterday()} onChange={(event) => updatePeriod(index, "endDate", event.target.value)}/></label>
        {periods.length > 1 && <button type="button" className="icon-button period-remove" aria-label="Remove period"
          onClick={() => setPeriods((current) => current.filter((_, position) => position !== index))}><Trash2 size={16}/></button>}
      </div>)}</div>
      <div className={`batch-preview ${large ? "warning" : ""}`}><strong>{dates.length} analysis {dates.length === 1 ? "date" : "dates"}</strong>
        <span>{tooLarge ? `The maximum is ${MAX_BATCH_DATES} dates. Split this into smaller batches.`
          : large ? "Large batches can take several hours and use more provider quota."
            : "At most two analyses will run at the same time."}</span></div>
      {large && !tooLarge && <label className="check-row"><input type="checkbox" checked={confirmedLargeBatch}
        onChange={(event) => setConfirmedLargeBatch(event.target.checked)}/><span><strong>Confirm large batch</strong>
          <small>I understand that this request contains more than 30 analysis dates.</small></span></label>}
      <label>Execution time <span className="optional">optional</span><input type="datetime-local" value={executeAt} onChange={(event) => setExecuteAt(event.target.value)}/><small>Leave blank to put this batch in the queue immediately.</small></label>
      <label className="check-row"><input type="checkbox" checked={force} onChange={(event) => setForce(event.target.checked)}/><span><strong>Refresh provider data</strong><small>Ignore valid cached facts and contact providers again.</small></span></label>
      {error && <p className="form-error">{error}</p>}
      <div className="modal-actions"><button type="button" className="button secondary" onClick={onClose}>Cancel</button><button className="button primary"
        disabled={saving || !dates.length || tooLarge || (large && !confirmedLargeBatch)}><CalendarClock size={16}/>{saving ? "Submitting…" : executeAt ? "Schedule batch" : "Queue batch"}</button></div>
    </form></section></div>;
}

function expandCalendarDates(periods: Period[]): string[] {
  const dates = new Set<string>();
  for (const period of periods) {
    if (!period.startDate || !period.endDate || period.startDate > period.endDate) continue;
    for (let current = new Date(`${period.startDate}T00:00:00Z`);
      current <= new Date(`${period.endDate}T00:00:00Z`);
      current = new Date(current.valueOf() + 86_400_000)) {
      dates.add(current.toISOString().slice(0, 10));
    }
  }
  return [...dates];
}

function yesterday(): string {
  const value = new Date();
  value.setDate(value.getDate() - 1);
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
}
